import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AnalysisRequest,
  type AnalysisRequestInput,
  PlanRequest,
  type AnalysisResult,
  type ChatMessage,
  type ChatToolName,
  type ChipPlan,
  type Position,
  type RequestedObjective,
  type Squad,
  type SquadPlayer,
  type SquadValidation,
  type TransferPlan,
} from '@fantasy/types';
import { api, ApiRequestError, type CreateSquadResponse, type UnmatchedScreenshotPlayer } from '../api.ts';
import { AuthContext } from '../auth/AuthContext.tsx';
import { buildSquadInput } from '../parseSquad.ts';

type Phase = 'idle' | 'working' | 'done';

/** Used until the real current gameweek loads — 1, never a later guess, so a
 * slow or failed fetch never implies matches have already been played. */
const FALLBACK_GAMEWEEK = 1;

const DEFAULT_PLAN_REQUEST = PlanRequest.parse({});
const DEFAULT_ANALYSIS_REQUEST = AnalysisRequest.parse({});

/** One turn of the argue-with-the-verdict conversation, as kept client-side
 * — `ChatMessage` plus the tools an assistant turn was built from, so
 * `AnalysisChat` can show its working. `toolsUsed` is only ever set on an
 * assistant entry; a user entry never carries it. */
export interface ChatEntry extends ChatMessage {
  toolsUsed?: ChatToolName[];
}

export interface SquadContextValue {
  squad: Squad | null;
  name: string;
  gameweek: number;

  // Set as soon as the mount-time restore below learns the account has
  // *any* saved squad — before the specific one has finished loading — so
  // a caller like SquadRoute doesn't flash onboarding UI for a returning
  // manager while that fetch is still in flight.
  hasSavedSquads: boolean;
  editorPlayers: Partial<Record<Position, SquadPlayer[]>>;
  unmatched: UnmatchedScreenshotPlayer[];
  editOpen: boolean;
  phase: Phase;
  problems: string[];
  validation: SquadValidation | null;
  analysis: AnalysisResult | null;

  // The conversation under the verdict (T-064) — independent of `analysis`
  // itself (a separate request per turn) but cleared alongside it whenever a
  // different squad is loaded, same reasoning as `plan`/`chipPlan`: an
  // argument about one squad's verdict must never linger under another's.
  chatMessages: ChatEntry[];
  chatPending: boolean;
  chatProblem: string | null;

  // The multi-week transfer plan — independent of `analysis` (a separate
  // request, priced and rendered separately) but cleared alongside it
  // whenever a different squad is loaded, same reasoning as `analysis`: a
  // plan from one squad must never linger under another.
  plan: TransferPlan | null;
  planning: boolean;
  planProblems: string[];
  planHorizon: number;
  planAllowHits: boolean;

  // The chip calendar — the transfer plan's sibling, same clearing rule:
  // independent of `analysis`/`plan` but wiped alongside them whenever a
  // different squad loads, so a calendar computed for one squad never lingers
  // under another.
  chipPlan: ChipPlan | null;
  chipPlanning: boolean;
  chipPlanProblems: string[];

  /** The manager's stated risk appetite for the next "Analyse my squad" call
   * — lives here rather than in `SquadView`'s own state so it survives that
   * component unmounting, the same reasoning as `planHorizon`/`planAllowHits`.
   * Includes `'auto'` (T-077): let the manager's rank pick it. Reset by
   * `adoptSquad` to a default that fits the squad just loaded — `'auto'` for
   * one with an FPL entry id to read a rank from, `'balanced'` otherwise,
   * since `'auto'` has nothing to work from without one. */
  objective: RequestedObjective;

  /** The manager's own mini-league to measure effective ownership against
   * (T-078) — `null` until one is picked. Lives here, not in
   * `LeagueStandings`' own state, so the league chosen there is the same one
   * `analyseSquad`/`runTheDesk` send as `leagueId`. */
  leagueId: number | null;

  // Bumped whenever a squad is created or imported, so SquadLibrary's own
  // list refetches and picks it up instead of going stale until the next
  // mount.
  libraryRefresh: number;

  // Whatever was active immediately before switching to a different squad
  // via the library — one level, not a full history stack. Lets "Back to
  // {name}" undo exactly one library switch; browsing to a second squad
  // from there replaces this with the one just left, it doesn't chain.
  previousSquad: { squad: Squad; validation: SquadValidation } | null;

  setEditOpen: (open: boolean) => void;
  setEditorPlayers: (players: Partial<Record<Position, SquadPlayer[]>>) => void;
  setUnmatched: (players: UnmatchedScreenshotPlayer[]) => void;
  setPlanHorizon: (horizon: number) => void;
  setPlanAllowHits: (allow: boolean) => void;
  setObjective: (objective: RequestedObjective) => void;
  setLeagueId: (leagueId: number | null) => void;

  /** SquadEditor's submit — builds a SquadInput from the assembled players
   * and saves it, exactly like the old CSV path did. Deliberately does NOT
   * analyse: saving and analysing are two separate actions, so editing a
   * squad never spends anything on its own. "Analyse my squad" on the squad
   * view is the only trigger. */
  saveSquad: (
    players: SquadPlayer[],
    meta: { name: string; gameweek: number; bank: number; freeTransfers: number },
  ) => Promise<void>;

  /** Shared by both "hand me an already-created squad" entry points — library
   * load and screenshot import. Puts it in state so SquadView can render it
   * immediately and syncs the form fields. Does NOT analyse — importing or
   * loading a squad never spends anything on its own; analysis is always the
   * explicit "Analyse my squad" action. */
  adoptSquad: (loaded: Squad, squadValidation: SquadValidation) => void;

  /** Screenshot import creates a brand new squad — bump the library's
   * refresh signal so it shows up in "Saved squads" afterward, unlike a
   * library load which is already there. */
  importSquad: (loaded: Squad, squadValidation: SquadValidation) => void;

  /** Loading from the library — the squad is already saved server-side, so
   * no refresh is needed. Remembers whatever was active before the switch
   * (as long as it's genuinely a different squad) so "Back to {name}" can
   * undo it. */
  loadFromLibrary: (loaded: Squad, squadValidation: SquadValidation) => void;

  /** The explicit "yes, replace what I'm holding with what FPL now has"
   * action (T-126) — fetches the linked squad's current picks from FPL and
   * updates it in place, same id, same name. A transfer changes who's in the
   * squad, so the result is handed to `adoptSquad` exactly like a library
   * load: any analysis/plan/chip-plan/chat for the eleven just replaced must
   * not linger under the new one. Errors surface in
   * `updateToGameweekProblem` rather than throwing, same pattern every other
   * squad-mutating action here follows. */
  updateToGameweek: (id: string) => Promise<void>;
  updatingToGameweek: boolean;
  updateToGameweekProblem: string | null;

  /** Set instead of updating the squad when the manual button finds FPL
   * hasn't genuinely advanced past this squad's own gameweek yet (T-127) —
   * a plain explanation, not `updateToGameweekProblem`: nothing failed and
   * nothing was lost, there was simply nothing yet to update to. */
  updateToGameweekNotice: string | null;

  /** Checks whether FPL has genuinely advanced this squad's own gameweek
   * since it was loaded, without waiting for a manager to navigate away and
   * back (T-127) — `SquadView` calls this once whenever the squad it's
   * showing (re)mounts, so a manager actually looking at their squad when
   * the advance happens finds out then, not just on the next full reload.
   * Reuses `GET /:id` — the same passive refresh every other load already
   * runs — aimed at the squad already on screen rather than one being
   * freshly loaded. A no-op for a squad with no `entryId`, or when nothing
   * has actually moved. On a genuine advance, behaves exactly like any
   * other squad swap (`adoptSquad`) and leaves a one-time
   * `gameweekAdvanceNotice` describing it. */
  checkForGameweekAdvance: (id: string) => Promise<void>;

  /** Set once by `checkForGameweekAdvance` when it finds a genuine advance —
   * cleared by `adoptSquad` on the next squad swap of any kind, so it's
   * never shown again for a squad already sitting on its new, now-current,
   * gameweek. */
  gameweekAdvanceNotice: string | null;

  /** Undoes exactly one library switch — see `previousSquad`'s own comment
   * for why this isn't a full history stack. */
  returnToPreviousSquad: () => void;

  /** Re-runs analysis on the squad already loaded — the only thing in the app
   * that calls the AI. Reachable at any time, independent of having just
   * imported or edited anything. `options` comes from SquadView's own
   * horizon/max-transfers/allow-hits controls, not a fixed value here. */
  analyseSquad: (id: string, options: AnalysisRequestInput) => Promise<void>;

  /** Requests a multi-week transfer plan for the loaded squad — the horizon
   * and allow-hits toggle come from `planHorizon`/`planAllowHits` above. A
   * failure leaves any previously rendered plan on screen untouched; it only
   * clears on a genuinely new fetch. */
  requestPlan: (id: string) => Promise<void>;

  /** Requests the chip calendar for the loaded squad — `plan`'s sibling, same
   * "a failure leaves whatever was previously rendered untouched" rule. */
  requestChipPlan: (id: string) => Promise<void>;

  /** `ThisWeek`'s one-button routine (T-075) — runs analysis, the transfer
   * plan and the chip plan together for the loaded squad, composing the
   * three functions above rather than duplicating their request logic. Each
   * of them already reports its own failure into its own problem state and
   * never throws, so this behaves exactly like running each independently:
   * one failing leaves the other two's results on screen, with only that
   * one's problem state set. */
  runTheDesk: (id: string) => Promise<void>;

  /** Sends one turn of the argument, appending both the manager's message
   * and the reply to `chatMessages` on success. Resolves `true` on success
   * and `false` on failure so `AnalysisChat` knows whether to clear its
   * input — a failure leaves `chatMessages` untouched and surfaces
   * `chatProblem` instead, so nothing typed is lost. */
  sendChatMessage: (squadId: string, message: string) => Promise<boolean>;
}

export const SquadContext = createContext<SquadContextValue | null>(null);

export function SquadProvider({ children }: { children: ReactNode }) {
  const [name, setName] = useState('');
  const [gameweek, setGameweek] = useState(FALLBACK_GAMEWEEK);
  const [editOpen, setEditOpen] = useState(false);
  const [editorPlayers, setEditorPlayers] = useState<Partial<Record<Position, SquadPlayer[]>>>({});
  const [unmatched, setUnmatched] = useState<UnmatchedScreenshotPlayer[]>([]);

  const [phase, setPhase] = useState<Phase>('idle');
  const [problems, setProblems] = useState<string[]>([]);
  const [validation, setValidation] = useState<SquadValidation | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [squad, setSquad] = useState<Squad | null>(null);

  const [chatMessages, setChatMessages] = useState<ChatEntry[]>([]);
  const [chatPending, setChatPending] = useState(false);
  const [chatProblem, setChatProblem] = useState<string | null>(null);

  const [plan, setPlan] = useState<TransferPlan | null>(null);
  const [planning, setPlanning] = useState(false);
  const [planProblems, setPlanProblems] = useState<string[]>([]);
  const [planHorizon, setPlanHorizon] = useState(DEFAULT_PLAN_REQUEST.horizon);
  const [planAllowHits, setPlanAllowHits] = useState(DEFAULT_PLAN_REQUEST.allowHits);
  // Reset to a squad-appropriate default by `adoptSquad` below — this initial
  // value only ever matters before any squad has loaded.
  const [objective, setObjective] = useState<RequestedObjective>(DEFAULT_ANALYSIS_REQUEST.objective);
  const [leagueId, setLeagueId] = useState<number | null>(null);

  const [chipPlan, setChipPlan] = useState<ChipPlan | null>(null);
  const [chipPlanning, setChipPlanning] = useState(false);
  const [chipPlanProblems, setChipPlanProblems] = useState<string[]>([]);

  const [libraryRefresh, setLibraryRefresh] = useState(0);
  const [previousSquad, setPreviousSquad] = useState<{ squad: Squad; validation: SquadValidation } | null>(
    null,
  );

  const [hasSavedSquads, setHasSavedSquads] = useState(false);

  const [updatingToGameweek, setUpdatingToGameweek] = useState(false);
  const [updateToGameweekProblem, setUpdateToGameweekProblem] = useState<string | null>(null);
  const [updateToGameweekNotice, setUpdateToGameweekNotice] = useState<string | null>(null);
  const [gameweekAdvanceNotice, setGameweekAdvanceNotice] = useState<string | null>(null);

  // `useContext` directly, not the throwing `useAuth()` — a handful of
  // lighter-weight tests render `SquadProvider` with no `AuthProvider`
  // ancestor at all, and this must keep working for them exactly as before
  // rather than crashing. Where there genuinely is one (the real app, and
  // every test that renders through it), its `status` is what the restore
  // effect below waits on.
  const authContext = useContext(AuthContext);
  const hasRestoredRef = useRef(false);

  // Mirrors `squad` so the restore effect below can read the *current*
  // value from inside an async callback — the `squad` the effect closed
  // over at mount is always null, which is exactly what it must not trust.
  const squadRef = useRef<Squad | null>(null);
  useEffect(() => {
    squadRef.current = squad;
  }, [squad]);

  // Defaults the gameweek field to whatever's actually current — the season
  // may not have started, or may be mid-week — rather than a fixed guess.
  // Only runs once; a squad loaded afterward sets its own gameweek anyway.
  useEffect(() => {
    api
      .currentGameweek()
      .then(({ currentGameweek }) => setGameweek(currentGameweek))
      .catch(() => {
        // Network hiccup — the safe fallback set at mount stays in place.
      });
  }, []);

  // Restores the account's most recently active squad on first mount,
  // whichever route a visit lands on (T-075) — this used to live in
  // SquadRoute's own mount effect, which left `/` (the This Week desk)
  // looking like no squad was ever loaded until a manager clicked through
  // to `/squad` first. The account's most recently created squad is still
  // saved server-side (`GET /api/squads` sorts newest first), so restore it
  // rather than leaving the screen empty with the squad only reachable by
  // scrolling to "Saved squads" and clicking it again.
  //
  // Only ever runs once, but a squad chosen explicitly during the two
  // network round-trips this takes — an import, a library load, a
  // from-scratch save — must win: `squadRef.current`, not the `squad` this
  // effect closed over at mount, is what gets checked immediately before
  // adopting.
  //
  // `SquadProvider` sits above the auth gate (`main.tsx`), so this fires on
  // first mount regardless of whether anyone is signed in yet — waiting for
  // `authContext.status === 'authenticated'` (when there's an `AuthContext`
  // to wait on) stops it firing an account-scoped request before a session
  // has actually been restored.
  useEffect(() => {
    if (authContext && authContext.status !== 'authenticated') return;
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    api
      .listSquads()
      .then(({ squads }) => {
        const [mostRecent] = squads;
        if (!mostRecent) return;
        setHasSavedSquads(true);
        if (squadRef.current) return;
        return api
          .getSquad(mostRecent.id)
          .then(({ squad: loaded, validation: squadValidation }) => {
            if (squadRef.current) return;
            adoptSquad(loaded, squadValidation);
          });
      })
      .catch(() => {
        // Not fatal — still reachable manually via "Saved squads".
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authContext?.status]);

  async function saveSquad(
    players: SquadPlayer[],
    meta: { name: string; gameweek: number; bank: number; freeTransfers: number },
  ) {
    setProblems([]);
    setPhase('working');
    try {
      const input = buildSquadInput(players, meta);
      const created = await api.createSquad(input);
      setValidation(created.validation);
      setAnalysis(null);
      // Same reasoning as adoptSquad: a plan computed for the squad being
      // replaced must not linger under the newly saved one.
      setPlan(null);
      setPlanProblems([]);
      setChipPlan(null);
      setChipPlanProblems([]);
      // Same reasoning again: an argument about the squad being replaced
      // must not linger under the newly saved one.
      setChatMessages([]);
      setChatProblem(null);
      setSquad(created.squad);
      setName(meta.name);
      setGameweek(meta.gameweek);
      setEditOpen(false);
      setLibraryRefresh((n) => n + 1);
      setPhase('idle');
    } catch (error) {
      setPhase('idle');
      setProblems([
        error instanceof ApiRequestError
          ? error.message
          : 'Something went wrong saving that squad.',
      ]);
    }
  }

  function adoptSquad(loaded: Squad, squadValidation: SquadValidation) {
    setProblems([]);
    setAnalysis(null);
    // A one-time advance notice belongs to the squad it was raised about —
    // any other squad swap (a genuinely different squad, or this same one
    // updated again) must not leave a stale "just moved to Gameweek N" behind.
    // `checkForGameweekAdvance` re-sets this itself, after calling adoptSquad.
    setGameweekAdvanceNotice(null);
    // A plan from whatever squad was previously loaded must never be shown
    // under this one — cleared here, not just left to be overwritten by the
    // next "Plan my transfers" click, since that click might never come.
    setPlan(null);
    setPlanProblems([]);
    // Same reasoning, same trigger, as the transfer plan above.
    setChipPlan(null);
    setChipPlanProblems([]);
    // Same reasoning again — see the ChatEntry state's own comment.
    setChatMessages([]);
    setChatProblem(null);
    setValidation(squadValidation);
    setSquad(loaded);
    setName(loaded.name);
    setGameweek(loaded.gameweek);
    // 'auto' only means anything for a squad with a rank to read — default to
    // it there, since deciding from the manager's actual standing beats a
    // fixed guess; fall back to 'balanced' for one with no FPL identity,
    // where 'auto' would have nothing to work from (see resolveObjective).
    setObjective(loaded.entryId !== undefined ? 'auto' : 'balanced');
  }

  function importSquad(loaded: Squad, squadValidation: SquadValidation) {
    setEditOpen(false);
    setLibraryRefresh((n) => n + 1);
    // A fresh import replaces the whole notion of "current squad" — there's
    // nothing left to offer "Back to" once it's superseded like this.
    setPreviousSquad(null);
    adoptSquad(loaded, squadValidation);
  }

  function loadFromLibrary(loaded: Squad, squadValidation: SquadValidation) {
    setEditOpen(false);
    if (squad && validation && squad.id !== loaded.id) {
      setPreviousSquad({ squad, validation });
    }
    adoptSquad(loaded, squadValidation);
  }

  function returnToPreviousSquad() {
    if (!previousSquad) return;
    setEditOpen(false);
    adoptSquad(previousSquad.squad, previousSquad.validation);
    setPreviousSquad(null);
  }

  async function updateToGameweek(id: string) {
    setUpdatingToGameweek(true);
    setUpdateToGameweekProblem(null);
    setUpdateToGameweekNotice(null);
    try {
      // `reason` is the T-127 addition — set instead of updating anything
      // when FPL hasn't genuinely advanced past this squad's own gameweek
      // yet. `api.updateToGameweek` is still typed as `CreateSquadResponse`
      // (`{squad, validation}`); read loosely here rather than widening that
      // type for every other caller of it.
      const response = (await api.updateToGameweek(id)) as CreateSquadResponse & { reason: string | null };
      if (response.reason) {
        setUpdateToGameweekNotice(response.reason);
        return;
      }
      // Same clearing rule as any other squad swap — see adoptSquad.
      adoptSquad(response.squad, response.validation);
    } catch (error) {
      setUpdateToGameweekProblem(
        error instanceof ApiRequestError
          ? error.message
          : 'Something went wrong updating this squad to the current gameweek.',
      );
    } finally {
      setUpdatingToGameweek(false);
    }
  }

  async function checkForGameweekAdvance(id: string) {
    try {
      const { squad: loaded, validation: loadedValidation } = await api.getSquad(id);
      // The squad on screen may have changed (or been cleared) while this
      // was in flight — same race `squadRef` already guards against for the
      // mount-time restore below. Only a genuine advance for the squad still
      // actually on screen counts.
      if (!squadRef.current || squadRef.current.id !== id || loaded.gameweek <= squadRef.current.gameweek) {
        return;
      }
      adoptSquad(loaded, loadedValidation);
      setGameweekAdvanceNotice(`This squad just moved to Gameweek ${loaded.gameweek}.`);
    } catch {
      // Best-effort — a failed check just means this look didn't catch it;
      // the squad already on screen is untouched either way.
    }
  }

  async function analyseSquad(id: string, options: AnalysisRequestInput) {
    setPhase('working');
    try {
      const { analysis: result } = await api.analyse(id, options);
      setAnalysis(result);
      setPhase('done');
    } catch (error) {
      setPhase('idle');
      setProblems([
        error instanceof ApiRequestError
          ? error.message
          : 'Something went wrong analysing that squad.',
      ]);
    }
  }

  async function requestPlan(id: string) {
    setPlanning(true);
    setPlanProblems([]);
    try {
      const { plan: result } = await api.plan(id, { horizon: planHorizon, allowHits: planAllowHits });
      setPlan(result);
    } catch (error) {
      setPlanProblems([
        error instanceof ApiRequestError ? error.message : 'Something went wrong planning transfers.',
      ]);
    } finally {
      setPlanning(false);
    }
  }

  async function requestChipPlan(id: string) {
    setChipPlanning(true);
    setChipPlanProblems([]);
    try {
      const { chipPlan: result } = await api.getChipPlan(id);
      setChipPlan(result);
    } catch (error) {
      setChipPlanProblems([
        error instanceof ApiRequestError ? error.message : 'Something went wrong planning chips.',
      ]);
    } finally {
      setChipPlanning(false);
    }
  }

  async function runTheDesk(id: string): Promise<void> {
    await Promise.all([
      analyseSquad(id, { ...DEFAULT_ANALYSIS_REQUEST, objective, leagueId: leagueId ?? undefined }),
      requestPlan(id),
      requestChipPlan(id),
    ]);
  }

  async function sendChatMessage(squadId: string, message: string): Promise<boolean> {
    setChatPending(true);
    setChatProblem(null);
    try {
      // The server only ever sees history it hasn't already been sent —
      // `message` is appended separately, not part of this.
      const history: ChatMessage[] = chatMessages.map(({ role, content }) => ({ role, content }));
      const { chat } = await api.chat(squadId, { history, message });
      setChatMessages((prev) => [
        ...prev,
        { role: 'user', content: message },
        { role: 'assistant', content: chat.reply, toolsUsed: chat.toolsUsed },
      ]);
      return true;
    } catch (error) {
      setChatProblem(
        error instanceof ApiRequestError ? error.message : 'Something went wrong sending that message.',
      );
      return false;
    } finally {
      setChatPending(false);
    }
  }

  const value: SquadContextValue = {
    squad,
    name,
    gameweek,
    hasSavedSquads,
    editorPlayers,
    unmatched,
    editOpen,
    phase,
    problems,
    validation,
    analysis,
    chatMessages,
    chatPending,
    chatProblem,
    plan,
    planning,
    planProblems,
    planHorizon,
    planAllowHits,
    chipPlan,
    chipPlanning,
    chipPlanProblems,
    objective,
    leagueId,
    libraryRefresh,
    previousSquad,
    setEditOpen,
    setEditorPlayers,
    setUnmatched,
    setPlanHorizon,
    setPlanAllowHits,
    setObjective,
    setLeagueId,
    updatingToGameweek,
    updateToGameweekProblem,
    updateToGameweekNotice,
    checkForGameweekAdvance,
    gameweekAdvanceNotice,
    saveSquad,
    adoptSquad,
    importSquad,
    loadFromLibrary,
    returnToPreviousSquad,
    updateToGameweek,
    analyseSquad,
    requestPlan,
    requestChipPlan,
    runTheDesk,
    sendChatMessage,
  };

  return <SquadContext.Provider value={value}>{children}</SquadContext.Provider>;
}
